import {
  parseQuickSwitcherScopedQuery,
  type QuickSearchScope,
} from "@/lib/quickSwitcherScopedQuery";

export type PaperworkSearchBatchItem = {
  batch_item_id: number | string;
  sku: string | null;
  build_order_number: string | null;
  mv_number: string | null;
};

export type PaperworkSearchSalesOrder = {
  sales_order_number: string;
  customer_purchase_order_number: string | null;
  client_name: string | null;
  batch_items: PaperworkSearchBatchItem[];
};

export type PaperworkSearchShipment = {
  shipment_id: number;
  batch_id: number | null;
  manufacturer_sales_order_number: string | null;
  carrier: string | null;
  note: string | null;
  tracking: string[];
  sales_orders: PaperworkSearchSalesOrder[];
};

function joinSearchParts(parts: Array<string | number | null | undefined>): string {
  return parts
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function effectivePaperworkSearchScope(scope: QuickSearchScope): QuickSearchScope {
  if (scope === "page" || scope === "wiki" || scope === "field") {
    return "all";
  }
  return scope;
}

export function paperworkSearchHaystack(shipment: PaperworkSearchShipment): string {
  const parts: Array<string | number | null | undefined> = [
    shipment.shipment_id,
    shipment.batch_id,
    shipment.manufacturer_sales_order_number,
    shipment.carrier,
    ...shipment.tracking.map((part) => part.trim()).filter((part) => part.length > 0),
    shipment.note,
  ];
  for (const salesOrder of shipment.sales_orders ?? []) {
    parts.push(
      salesOrder.sales_order_number,
      salesOrder.customer_purchase_order_number,
      salesOrder.client_name,
    );
    for (const item of salesOrder.batch_items ?? []) {
      parts.push(item.batch_item_id, item.sku, item.build_order_number, item.mv_number);
    }
  }
  return joinSearchParts(parts);
}

export function paperworkSearchHaystackForScope(
  shipment: PaperworkSearchShipment,
  scope: QuickSearchScope,
): string {
  switch (scope) {
    case "shipment":
      return joinSearchParts([
        shipment.shipment_id,
        shipment.carrier,
        ...shipment.tracking.map((part) => part.trim()).filter((part) => part.length > 0),
        shipment.note,
      ]);
    case "batch":
      return joinSearchParts([shipment.batch_id, shipment.manufacturer_sales_order_number]);
    case "so":
      return joinSearchParts(
        shipment.sales_orders.flatMap((salesOrder) => [
          salesOrder.sales_order_number,
          salesOrder.client_name,
        ]),
      );
    case "po":
      return joinSearchParts(
        shipment.sales_orders.map((salesOrder) => salesOrder.customer_purchase_order_number),
      );
    case "sku":
      return joinSearchParts(
        shipment.sales_orders.flatMap((salesOrder) =>
          salesOrder.batch_items.flatMap((item) => [
            item.batch_item_id,
            item.sku,
            item.build_order_number,
            item.mv_number,
          ]),
        ),
      );
    case "all":
    default:
      return paperworkSearchHaystack(shipment);
  }
}

export function parsePaperworkSearchQuery(raw: string): { scope: QuickSearchScope; term: string } {
  return parseQuickSwitcherScopedQuery(raw);
}

export function matchesPaperworkSearch(
  shipment: PaperworkSearchShipment,
  rawQuery: string,
): boolean {
  const parsed = parsePaperworkSearchQuery(rawQuery);
  const term = parsed.term.trim().toLowerCase();
  const scope = effectivePaperworkSearchScope(parsed.scope);

  if (!term) {
    return scope === "all";
  }

  return paperworkSearchHaystackForScope(shipment, scope).includes(term);
}

export function filterPaperworkShipments<T extends PaperworkSearchShipment>(
  shipments: T[],
  rawQuery: string,
): T[] {
  const trimmed = rawQuery.trim();
  if (!trimmed) {
    return shipments;
  }
  return shipments.filter((shipment) => matchesPaperworkSearch(shipment, trimmed));
}
